import cx from 'classnames'
import dynamic from 'next/dynamic'
import { ReactNode, useContext } from 'react'

import { type SanityTeamMember } from '@data/sanity/queries/types/blocks'
import { getLinkPageUrl } from '@lib/routes'
import { StringsContext } from '@lib/strings'

import ArrowRightIcon from '@components/icons/arrow-right'
import SimpleLink from '@components/simple-link'

const Photo = dynamic(() => import('@components/photo'))

interface TeamMemberCardProps {
  teamMember: SanityTeamMember
}

const TeamMemberCard = ({ teamMember }: TeamMemberCardProps) => {
  const strings = useContext(StringsContext)

  if (!teamMember) {
    return null
  }

  const { name, slug, jobTitle, photo, linkedInUrl, blogAuthor } = teamMember
  const blogAuthorPageUrl = blogAuthor
    ? getLinkPageUrl('blogAuthor', blogAuthor.slug)
    : null

  return (
    <div className="flex flex-col items-center relative">
      {photo && (
        <Photo
          image={photo}
          className="rounded-full overflow-hidden max-w-[60%]"
          imageClassName="object-cover"
        />
      )}
      {!photo && <PlaceholderImage />}

      <h5 className="text-2xl mt-3">{name}</h5>
      {jobTitle && <p className="text-base">{jobTitle}</p>}

      <hr className="my-3 bg-green w-[40px]" />

      <div className="flex nowrap items-center gap-x-3">
        {linkedInUrl && (
          <Link
            id={`team-member-${slug}-card-linkedin-link`}
            href={linkedInUrl}
            openInNewTab
          >
            {strings.teamMemberCardLinkedIn}
          </Link>
        )}

        {blogAuthorPageUrl && (
          <Link
            id={`team-member-${slug}-card-blog-link`}
            href={blogAuthorPageUrl}
          >
            {strings.teamMemberCardBlogArticles}
          </Link>
        )}
      </div>
    </div>
  )
}

interface LinkProps {
  children: ReactNode
  id: string
  href: string
  color?: 'green' | 'white'
  openInNewTab?: boolean
  hasArrowIcon?: boolean
}

const Link = ({
  children,
  id,
  href,
  color = 'green',
  openInNewTab,
  hasArrowIcon,
}: LinkProps) => {
  const arrowIcon = hasArrowIcon ? (
    <ArrowRightIcon id={`icon-${id}`} className="text-yellow text-xl" />
  ) : null

  const classes = cx('hover:underline', {
    'inline-flex items-center gap-x-2': arrowIcon,
    'text-white': color === 'white',
    'text-green': color === 'green',
  })

  if (openInNewTab) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={classes}>
        {arrowIcon}
        {children}
      </a>
    )
  }

  return (
    <SimpleLink href={href} className={classes}>
      {arrowIcon}
      {children}
    </SimpleLink>
  )
}

const PlaceholderImage = () => (
  <svg
    width="200"
    height="200"
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M185 152L15 152C32.4922 180.788 64.0169 200 100 200C135.983 200 167.508 180.788 185 152Z"
      fill="#E7FF86"
    />
    <path
      opacity="0.6"
      d="M14.6262 152C5.34753 136.837 1.44202e-06 119.011 2.27581e-06 99.9359C4.68837e-06 44.7429 44.7715 0.000283132 100 0.000285546C155.229 0.00028796 200 44.7429 200 99.9359C200 119.011 194.652 136.837 185.374 152L14.6262 152Z"
      fill="#8A9780"
    />
  </svg>
)

export default TeamMemberCard
